import { API } from '../configs/axios/axios.config'

const GET_STARTER_CODE_ENDPOINT = token => `/unit-tests/${token}/starter-code`
const SUBMIT_INSTANT_FEEDBACK_ENDPOINT = token => `/instant-feedback/${token}/submit`
const CHECK_STUDENT_SUBMISSION_ENDPOINT = token => `/instant-feedback/${token}/check-student-submission`
const GET_EXISTING_FEEDBACK_ENDPOINT = token => `/instant-feedback/${token}/get-existing-feedback`
const CREATE_ASSIGNMENT_STUDENT_FEEDBACK_ENDPOINT = `/assignment-feedback`
const GET_ASSIGNMENT_STUDENT_FEEDBACK_ENDPOINT = token => `/assignment-feedback/${token}/by-assignment`
const UPDATE_ASSIGNMENT_STUDENT_FEEDBACK_ENDPOINT = id => `/assignment-feedback/${id}`
const RECORD_METRICS_ENDPOINT = token => `/patterns/${token}/record-metrics`
const TEXT_INSTANT_FEEDBACK_ENDPOINT = token => `/instant-feedback/${token}/text-feedback`
const TEXT_INSTANT_FEEDBACK_CONFIG_ENDPOINT = token => `/assignment-metadata/${token}/instant-feedback-config`
const TEXT_INSTANT_FEEDBACK_INFO_ENDPOINT = token => `/questions/${token}/instant-feedback-info`
const TEXT_STUDENT_STATE_ENDPOINT = (token, userId) => `/instant-feedback/${token}/text-student-state/${userId}`
const TEXT_SAVE_DRAFTS_ENDPOINT = (token, userId) => `/instant-feedback/${token}/text-save-drafts/${userId}`
const TEXT_SUBMIT_FOR_GRADE_ENDPOINT = (token, userId) => `/instant-feedback/${token}/text-submit-for-grade/${userId}`
const TEXT_CHECK_APPROVAL_ENDPOINT = (token, userId) => `/instant-feedback/${token}/text-check-approval/${userId}`

class _InstantFeedbackService {
    getStarterCode(token, userId, instantFeedbackType) {
        return API.get(GET_STARTER_CODE_ENDPOINT(token), {
            timeout: 8000,
            params: {
                instant_feedback_type: instantFeedbackType,
                ...(userId !== undefined && { user_id: userId }),
            },
        })
    }

    submitSolution(token, solution, submissionToken, timeout) {
        return API.post(
            SUBMIT_INSTANT_FEEDBACK_ENDPOINT(token),
            {
                solution,
                submission_token: submissionToken,
            },
            {
                timeout,
            },
        )
    }

    getExistingFeedback(token, file_token) {
        return API.post(
            GET_EXISTING_FEEDBACK_ENDPOINT(token),
            {
                file_token: file_token,
            },
            {
                timeout: 15000,
            },
        )
    }

    checkStudentSubmission(token, studentId, classId) {
        return API.post(
            CHECK_STUDENT_SUBMISSION_ENDPOINT(token),
            {
                student_id: studentId,
                class_id: classId,
            },
            {
                timeout: 15000,
            },
        )
    }

    createAssignmentStudentFeedback(token, userId, liked) {
        return API.post(
            CREATE_ASSIGNMENT_STUDENT_FEEDBACK_ENDPOINT,
            {
                user_id: userId,
                liked,
            },
            {
                params: {
                    assignment_token: token,
                },
            },
        )
    }

    updateAssignmentStudentFeedback(id, feedback) {
        return API.patch(UPDATE_ASSIGNMENT_STUDENT_FEEDBACK_ENDPOINT(id), { feedback })
    }

    getAssignmentStudentsFeedback(token) {
        return API.get(GET_ASSIGNMENT_STUDENT_FEEDBACK_ENDPOINT(token))
    }

    recordPatternMetrics(token, fileId, actedPatterns) {
        return API.put(RECORD_METRICS_ENDPOINT(token), {
            acted_patterns: actedPatterns,
            file_id: fileId,
        })
    }

    /**
     * Get text instant feedback configuration for an assignment
     * @param {string} token - Assignment token
     * @returns {Promise} Configuration including showGrades, maxIterations, instructions, etc.
     */
    getTextInstantFeedbackConfig(token) {
        return API.get(TEXT_INSTANT_FEEDBACK_CONFIG_ENDPOINT(token), {
            timeout: 15000,
        })
    }

    /**
     * Get assignment info for text instant feedback (questions, rubrics, instructions)
     * @param {string} token - Assignment token
     * @param {string} userId - User ID for tracking iterations
     * @returns {Promise} Assignment info including questions and rubric structure
     */
    getTextInstantFeedbackInfo(token, userId) {
        return API.get(TEXT_INSTANT_FEEDBACK_INFO_ENDPOINT(token), {
            timeout: 15000,
            params: { user_id: userId },
        })
    }

    /**
     * Submit text for instant feedback classification
     * @param {string} token - Assignment token
     * @param {string} studentId - Student identifier
     * @param {Array<{question_id: number, text: string}>} responses - Question responses
     * @param {string} mode - 'anonymous' or 'stored'
     * @returns {Promise} Classification results organized by rubric criteria
     */
    submitTextInstantFeedback(token, studentId, responses, mode = 'anonymous') {
        return API.post(
            TEXT_INSTANT_FEEDBACK_ENDPOINT(token),
            {
                student_id: studentId,
                responses,
                mode,
            },
            {
                timeout: 120000, // 2 minutes for classification processing
            },
        )
    }

    // ==================== Authorized LTI Submission Lifecycle ====================

    /**
     * Get student's current state for text instant feedback (LTI authorized)
     * @param {string} token - Assignment token
     * @param {string} userId - LMS user ID
     * @returns {Promise} Student state including drafts, iterations used, submission status
     */
    getTextStudentState(token, userId) {
        return API.get(TEXT_STUDENT_STATE_ENDPOINT(token, userId), {
            timeout: 15000,
        })
    }

    /**
     * Save draft submissions (auto-save for LTI authorized users)
     * @param {string} token - Assignment token
     * @param {string} userId - LMS user ID
     * @param {Array<{question_id: number, text: string}>} drafts - Draft responses
     * @returns {Promise} Save confirmation
     */
    saveTextDrafts(token, userId, drafts) {
        return API.post(
            TEXT_SAVE_DRAFTS_ENDPOINT(token, userId),
            { drafts },
            { timeout: 15000 },
        )
    }

    /**
     * Submit text responses for final grading (LTI authorized)
     * @param {string} token - Assignment token
     * @param {string} userId - LMS user ID
     * @param {Array<{question_id: number, text: string}>} responses - Final responses
     * @returns {Promise} Submission confirmation
     */
    submitTextForGrade(token, userId, responses) {
        return API.post(
            TEXT_SUBMIT_FOR_GRADE_ENDPOINT(token, userId),
            { responses },
            { timeout: 30000 },
        )
    }

    /**
     * Check if student's submission has been reviewed by TA.
     * Used after submit-for-grade to check if TA has approved all classifications.
     * @param {string} token - Assignment token
     * @param {string} userId - LMS user ID
     * @returns {Promise} Status including pending_review and show_grades flags
     */
    checkApprovalStatus(token, userId) {
        return API.get(TEXT_CHECK_APPROVAL_ENDPOINT(token, userId), {
            timeout: 15000,
        })
    }
}

const InstantFeedbackService = new _InstantFeedbackService()
export default InstantFeedbackService
